<template>
  <el-input
    v-model="inner"
    :placeholder="placeholder"
    :disabled="disabled"
    :formatter="fmt"
    :parser="parse"
    class="money-input"
    @blur="onBlur"
  >
    <template v-if="unit" #suffix>{{ unit }}</template>
  </el-input>
</template>

<script setup>
// 금액 입력 공통 컴포넌트 — 천단위 콤마 표시(el-input formatter/parser),
// 값은 숫자로 유지(blur 시 정규화). el-input-number는 formatter 미지원이라 el-input 사용.
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: [Number, String], default: null },
  precision: { type: Number, default: 2 },
  min: { type: Number, default: 0 },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  unit: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

// el-input v-model: parser가 콤마를 제거한 순수 숫자 문자열이 담긴다.
const inner = ref('')

const fmt = (v) => {
  const s = (v ?? '').toString().replace(/,/g, '')
  if (s === '') return ''
  const neg = s.startsWith('-') ? '-' : ''
  const abs = neg ? s.slice(1) : s
  const [int, dec] = abs.split('.')
  const c = (int || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return neg + (dec !== undefined ? `${c}.${dec}` : c)
}
const parse = (v) => (v ?? '').toString().replace(/,/g, '')

watch(() => props.modelValue, (v) => {
  const s = (v === null || v === undefined || v === '') ? '' : String(v)
  if (s !== inner.value) inner.value = s
}, { immediate: true })

watch(inner, (v) => {
  const clean = (v ?? '').toString().replace(/[^0-9.-]/g, '')
  emit('update:modelValue', clean === '' ? null : clean)
})

const onBlur = () => {
  if (inner.value === '' || inner.value === null || inner.value === undefined) {
    emit('update:modelValue', null)
    return
  }
  let n = Number(String(inner.value).replace(/,/g, ''))
  if (Number.isNaN(n)) { inner.value = ''; emit('update:modelValue', null); return }
  if (n < props.min) n = props.min
  n = Number(n.toFixed(props.precision))
  inner.value = String(n)
  emit('update:modelValue', n)
}
</script>

<style scoped>
.money-input :deep(input) { text-align: right; }
</style>
